import { Link } from 'react-router-dom';
import { Card, ClickableIcon, Icon } from '@zenid/web';
import { useCallback, useContext } from 'react';
import { css, cx } from '../../../../../styled-system/css';
import { flex } from '../../../../../styled-system/patterns';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import type { EmailResendDialogProps } from '../../hooks/useEmailResendDialog';
import type { VerificationEmailProps } from '../../hooks/useVerificationEmail';
import type { MfaResetAlertDialogProps } from '../../hooks/useMfaResetAlertDialog';
import { ResendEmailAlert } from './resendEmail/ResendEmailAlert/ResendEmailAlert';
import { MfaResetAlertDialog } from './resetMfa/MfaResetAlertDialog/MfaResetAlertDialog';
import type { components } from '#/types/api/zenid-mypage-api';
import { SnackbarContext } from '#/utils/snackbar/snackbarProvider';

export type LoginAndSecurityProps = {
  account: components['schemas']['Models.Account'];
  emailResendDialog: EmailResendDialogProps;
  verificationEmail: VerificationEmailProps;
  mfaAlertDialog: MfaResetAlertDialogProps;
  authenticationMethods: Array<components['schemas']['Models.AuthenticationMethod']> | null;
};

export function LoginAndSecurity({
  account: { id, email, changePasswordUrl, emailVerified },
  emailResendDialog,
  verificationEmail,
  mfaAlertDialog: {
    isOpen: isOpenMfaAlertDialog,
    setIsOpen: setIsMfaAlertDialog,
    closeDialog: closeMfaAlertDialog,
    createMfaUrl,
    deleteMfaMethod,
  },
  authenticationMethods,
}: LoginAndSecurityProps) {
  const snackbar = useContext(SnackbarContext);
  const { copy } = useCopyToClipboard();
  const hasAuthenticationMethod = authenticationMethods === null ? false : authenticationMethods.length !== 0;
  const resetMfa = useCallback(async () => {
    const mfaEnrollmentUrl = await createMfaUrl();
    console.log(`mfaEnrollmentUrl=${mfaEnrollmentUrl}`);
    if (!mfaEnrollmentUrl) {
      snackbar.add({
        id: Date.now(),
        message: '二段階認証のリセットに失敗しました',
        notificationLevel: 'error',
        hideCloseButon: false,
      });
      return;
    }
    try {
      await deleteMfaMethod();
    } catch (e) {
      console.error(e);
      snackbar.add({
        id: Date.now(),
        message: '二段階認証のリセットに失敗しました',
        notificationLevel: 'error',
        hideCloseButon: false,
      });
      return;
    }
    const windowOpenSuccess = window.open(mfaEnrollmentUrl);
    if (windowOpenSuccess) {
      closeMfaAlertDialog();
    } else {
      // ポップアップブロックされた場合はリダイレクト
      location.href = mfaEnrollmentUrl;
    }
  }, [createMfaUrl, deleteMfaMethod, snackbar, closeMfaAlertDialog]);
  const handleOpenChange = useCallback(
    async (isOpen: boolean) => {
      if (!isOpen) {
        setIsMfaAlertDialog(false);
        return;
      }
      if (hasAuthenticationMethod) {
        setIsMfaAlertDialog(true);
        return;
      }
      setIsMfaAlertDialog(false);
      const mfaEnrollmentUrl = await createMfaUrl();
      if (!mfaEnrollmentUrl) {
        snackbar.add({
          id: Date.now(),
          message: '二段階認証の変更処理に失敗しました',
          notificationLevel: 'error',
          hideCloseButon: false,
        });
        return;
      }
      window.open(mfaEnrollmentUrl);
    },
    [createMfaUrl, setIsMfaAlertDialog, snackbar, hasAuthenticationMethod]
  );
  return (
    <div className={flex({ minWidth: '343px', flexDirection: 'column', sm: { maxWidth: '720px' } })}>
      <div
        className={flex({
          flexDirection: 'row',
          alignItems: 'center',
          gap: '{spacing.4}',
          marginBottom: '{spacing.20}',
        })}
      >
        <Icon name="lock" color="colors.text.main" size="m" />
        <h2 className={css({ textStyle: 'headding.s', sm: { textStyle: 'headding.l' } })}>ログインとセキュリティ</h2>
      </div>
      <Card className={flex({ backgroundColor: '{colors.background.white}' })}>
        <ul
          className={flex({
            width: '100%',
            flexDirection: 'column',
          })}
        >
          <li className={listItemStyle}>
            <div
              className={flex({
                width: '100%',
                flexDirection: 'column',
                borderBottom: '1px solid {colors.border.light}',
                padding: '{spacing.16} 0',
                gap: '{spacing.8}',
                sm: { flexDirection: 'row', padding: '{spacing.20} 0', gap: '{spacing.16}' },
              })}
            >
              <p className={itemNameStyle}>ユーザーID</p>
              <div className={flex({ width: '100%', alignItems: 'center', gap: '{spacing.8}', alignSelf: 'stretch' })}>
                <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })} data-dd-privacy="mask">
                  {id}
                </p>
                <ClickableIcon name="content_copy" color="colors.text.sub" size="s" onClick={() => copy(id)} />
              </div>
            </div>
          </li>
          <li
            className={cx(
              listItemStyle,
              css({
                _hover: {
                  background:
                    'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
                },
              })
            )}
          >
            <div
              className={flex({
                width: '100%',
                flexDirection: 'column',
                borderBottom: '1px solid {colors.border.light}',
                padding: '{spacing.16} 0',
                gap: '{spacing.12}',
                sm: { padding: '{spacing.20} 0' },
              })}
            >
              <Link to="/edit/email" data-testid="email-link">
                <div
                  className={flex({
                    width: '100%',
                    flexDirection: 'row',
                  })}
                >
                  <div
                    className={flex({
                      width: '100%',
                      gap: '{spacing.8}',
                      flexDirection: 'column',
                      sm: { flexDirection: 'row', gap: '{spacing.16}' },
                    })}
                  >
                    <p className={itemNameStyle}>メールアドレス</p>
                    <div className={flex({ flexDirection: 'column' })}>
                      <p
                        className={css({
                          textStyle: 'body.m.default',
                          color: '{colors.text.main}',
                          wordBreak: 'break-all',
                        })}
                        data-dd-privacy="mask"
                      >
                        {email}
                      </p>
                      <div className={flex({ alignItems: 'center' })}>
                        {emailVerified ? (
                          <>
                            <Icon name="check_fill_off" color="colors.action.success.main" size="s" />
                            <p className={css({ textStyle: 'caption.m', color: '{colors.text.sub}' })}>認証済</p>
                          </>
                        ) : (
                          <>
                            <Icon name="error_fill_off" color="colors.text.sub" size="s" />
                            <p className={css({ textStyle: 'caption.m', color: '{colors.text.sub}' })}>未認証</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={flex({ alignItems: 'center' })}>
                    <Icon name="navigate_next" color="colors.text.sub" size="s" />
                  </div>
                </div>
              </Link>
              {emailVerified || (
                <div className={css({ maxWidth: '468px', sm: { marginLeft: '156px' } })}>
                  <ResendEmailAlert
                    email={email}
                    verificationEmail={verificationEmail}
                    emailResendDialog={emailResendDialog}
                  />
                </div>
              )}
            </div>
          </li>
          <li
            className={cx(
              listItemStyle,
              css({
                _hover: {
                  background:
                    'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
                },
              })
            )}
          >
            <a href={changePasswordUrl} className={css({ width: '100%' })} data-testid="change-password-url">
              <div
                className={flex({
                  width: '100%',
                  flexDirection: 'row',
                  borderBottom: '1px solid {colors.border.light}',
                  padding: '{spacing.16} 0',
                  sm: { padding: '{spacing.20} 0' },
                })}
              >
                <div
                  className={flex({
                    width: '100%',
                    flexDirection: 'column',
                    gap: '{spacing.8}',
                    sm: { gap: '{spacing.16}', flexDirection: 'row' },
                  })}
                >
                  <p className={itemNameStyle}>パスワード</p>
                  <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })}>変更する</p>
                </div>
                <div className={flex({ alignItems: 'center' })}>
                  <Icon name="navigate_next" color="colors.text.sub" size="s" />
                </div>
              </div>
            </a>
          </li>
          <li
            className={cx(
              listItemStyle,
              css({
                _hover: {
                  background:
                    'linear-gradient(90deg, rgba(66, 137, 232, 0.00) 0%, rgba(66, 137, 232, 0.10) 8%, rgba(66, 137, 232, 0.10) 92%, rgba(66, 137, 232, 0.00) 100%)',
                },
              })
            )}
          >
            <MfaResetAlertDialog
              onOpenChange={handleOpenChange}
              open={isOpenMfaAlertDialog}
              onClickOk={resetMfa}
              onClickNG={closeMfaAlertDialog}
            >
              <button
                className={css({
                  width: '100%',
                  _hover: {
                    cursor: 'pointer',
                  },
                })}
              >
                <div
                  className={flex({
                    width: '100%',
                    flexDirection: 'row',
                    borderBottom: '1px solid {colors.border.light}',
                    padding: '{spacing.16} 0',
                    sm: { padding: '{spacing.20} 0' },
                  })}
                >
                  <div
                    className={flex({
                      width: '100%',
                      flexDirection: 'column',
                      gap: '{spacing.8}',
                      sm: { gap: '{spacing.16}', flexDirection: 'row' },
                    })}
                  >
                    <p className={itemNameStyle}>2段階認証</p>
                    <p className={css({ textStyle: 'body.m.default', color: '{colors.text.main}' })}>
                      {hasAuthenticationMethod ? 'リセット' : '設定する'}
                    </p>
                  </div>
                  <div className={flex({ alignItems: 'center' })}>
                    <Icon name="navigate_next" color="colors.text.sub" size="s" />
                  </div>
                </div>
              </button>
            </MfaResetAlertDialog>
          </li>
        </ul>
      </Card>
    </div>
  );
}

const listItemStyle = flex({
  position: 'relative',
  alignItems: 'center',
  alignSelf: 'stretch',
  margin: '0 {spacing.16}',
  sm: { margin: '0 {spacing.24}' },
});

const itemNameStyle = flex({
  width: '100%',
  maxWidth: '311px',
  alignItems: 'center',
  color: '{colors.text.main}',
  textStyle: 'body.s.defalut',
  sm: { maxWidth: '140px' },
});
